import { getAccessToken } from "../lib/auth";
import { parseApiError } from "../lib/api-error";

// Mirrors api/src/types/gateway.ts. Keep both in lockstep — same field
// names, same Zod constraints (the BE re-validates on submit so a UI bug
// can't ship invalid manifests).
export interface GatewayRouteSpec {
  app: string;
  component?: string;
  namespace?: string;
  host: string;
  pathPrefix: string;
  backend: {
    service: string;
    port: number;
  };
  auth: {
    required: boolean;
    optional: boolean;
    requiredRealmRoles: string[];
  };
  rateLimit?: {
    requests: number;
    unit: "Second" | "Minute" | "Hour" | "Day";
  };
}

export interface GatewayRouteSummary {
  spec: GatewayRouteSpec;
  files: {
    httpRoute: string;
    securityPolicy: string | null;
    backendTrafficPolicy: string | null;
  };
  openPrUrl: string | null;
  lastChangedAt: string | null;
}

export interface RenderedRoute {
  paths: {
    httpRoute: string;
    securityPolicy: string | null;
    backendTrafficPolicy: string | null;
  };
  yamls: {
    httpRoute: string;
    securityPolicy: string | null;
    backendTrafficPolicy: string | null;
  };
}

export interface PRResponse {
  pr: { number: number; url: string };
}

async function fetchAPI<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const token = await getAccessToken();
  const response = await fetch(`/api/gateway${endpoint}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options?.headers as Record<string, string>),
    },
  });
  if (!response.ok) throw await parseApiError(response);
  return response.json();
}

export const gatewayApi = {
  list: () => fetchAPI<{ routes: GatewayRouteSummary[] }>("/routes"),
  create: (spec: GatewayRouteSpec) =>
    fetchAPI<PRResponse>("/routes", {
      method: "POST",
      body: JSON.stringify(spec),
    }),
  update: (spec: GatewayRouteSpec) =>
    fetchAPI<PRResponse>("/routes", {
      method: "PATCH",
      body: JSON.stringify(spec),
    }),
  remove: (spec: GatewayRouteSpec) =>
    fetchAPI<PRResponse>("/routes", {
      method: "DELETE",
      body: JSON.stringify(spec),
    }),
  preview: (spec: GatewayRouteSpec) =>
    fetchAPI<RenderedRoute>("/routes/preview", {
      method: "POST",
      body: JSON.stringify(spec),
    }),
};
